<template>
  <div class="player">
    <div :class="'controls animations ' + [!onGoingAnimation ? 'disabled' : '']">
      <player-button
        :btnClassName="'fas ' + [animationsOn ? 'fa-stop' : 'fa-play']"
        :on-click="handleToggleAnimations"
        :ariaLabel="'Animations ' + [animationsOn ? 'On' : 'Off']"
        :ariaPressed="!animationsOn"
        :disabled="!onGoingAnimation"
      ></player-button>
    </div>
    <div class="controls mute">
      <player-button
        :btnClassName="'fas ' + [soundMuted ? 'fa-volume-mute' : 'fa-volume-up']"
        :on-click="handleMuteSound"
        :ariaLabel="'Mute'"
        :ariaPressed="soundMuted"
      ></player-button>
    </div>
    <div class="controls volume">
      <player-button
        :btnClassName="'fas fa-volume-down'"
        :on-click="handleVolumeDown"
        :ariaLabel="'Volume Down'"
      ></player-button>
      <span>{{ soundVolume * 100 }}%</span>
      <player-button
        :btnClassName="'fas fa-volume-up'"
        :on-click="handleVolumeUp"
        :ariaLabel="'Volume Up'"
      ></player-button>
    </div>
    <div :class="'controls caption-button ' + [showCaption ? '' : 'off']">
      <player-button
        :on-click="handleToggleCaption"
        :show-icon="false"
        :show-text="true"
        :textClassName="'caption-btn-text'"
        :text="'CC ' + [showCaption ? 'ON' : 'OFF']"
        :ariaLabel="'Closed Captions'"
        :ariaPressed="showCaption"
      >
      </player-button>
    </div>
    <div class="controls caption-size">
      <player-button
        :on-click="handleDecreaseCaption"
        :btnClassName="'fas fa-minus'"
        :ariaLabel="'Decrease captions font size'"
      >
      </player-button>
      <span>{{ captionSizePercentage }}%</span>
      <player-button
        :on-click="handleIncreaseCaption"
        :btnClassName="'fas fa-plus'"
        :ariaLabel="'Increase captions font size'"
      >
      </player-button>
    </div>
    <div v-if="showCaption" class="captions" :style="cssVars">
      <span :class="'caption-text ' + [caption ? 'padded' : '']">{{ caption }}</span>
    </div>
  </div>
</template>
<script>
import PlayerManager from 'mixins/PlayerManager';
import { createNamespacedHelpers } from 'vuex';
import PlayerButton from 'components/buttons/PlayerButton.vue';

const { mapState, mapMutations, mapActions } = createNamespacedHelpers('accessibility');

export default {
  components: {
    PlayerButton
  },

  mixins: [PlayerManager],

  data() {
    return {
      audio: {},
      caption: '',
      captionsArr: [],
      zoom: '100%',
      captionElement: ''
    };
  },
  computed: {
    ...mapState({
      soundPlaying: state => state.soundPlaying,
      soundMuted: state => state.soundMuted,
      soundVolume: state => state.soundVolume,
      showCaption: state => state.showCaption,
      captionSize: state => state.captionSize,
      captionSizePercentage: state => state.captionSizePercentage,
      animationsOn: state => state.animationsOn,
      onGoingAnimation: state => state.onGoingAnimation
    }),
    cssVars() {
      return {
        '--font-Size': `${this.captionSize}px`
      };
    }
  },
  created() {},
  mounted() {
    [this.captionElement] = document.getElementsByClassName('captions');
  },

  methods: {
    ...mapMutations([
      'playPauseSound',
      'muteSound',
      'stopSound',
      'setShowCaption',
      'setSoundVolume',
      'setCaptionFontSize',
      'setAnimationsMode'
    ]),
    ...mapActions([
      'togglePlaySound',
      'stopSound',
      'toggleMute',
      'toggleCaption',
      'setVolume',
      'resizeCaptionFontSize',
      'setFontSizePercentage',
      'toggleAnimations'
    ]),
    playSoundAndShowCaptions(audio, captions) {
      this.audio = audio;
      this.captionsArr = captions;
      if (this.soundMuted) this.audio.mute(this.soundMuted);
      else this.audio.volume(this.soundVolume);
      this.audio.play();
      this.playPauseSound(true);
      this.scrubAudio(this.audio, this.captionsArr);
    },
    handlePlaySound() {
      this.togglePlaySound(this.audio);
    },
    handleStopSound() {
      this.stopSound(this.audio);
    },
    handleMuteSound() {
      this.toggleMute(this.audio);
    },
    handleVolumeDown() {
      this.setVolume({ audioObj: this.audio, step: -0.1 });
    },
    handleVolumeUp() {
      this.setVolume({ audioObj: this.audio, step: 0.1 });
    },
    handleToggleCaption() {
      this.toggleCaption();
    },
    handleToggleAnimations() {
      this.toggleAnimations();
    },
    handleIncreaseCaption() {
      const { captionElement } = this;
      this.resizeCaptionFontSize({ captionElement, increment: 9 });
      this.setFontSizePercentage(50);
    },
    handleDecreaseCaption() {
      const { captionElement } = this;
      this.resizeCaptionFontSize({ captionElement, increment: -9 });
      this.setFontSizePercentage(-50);
    }
  }
};
</script>
<style scoped lang="scss">
.player {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  position: absolute;
  bottom: 15px;
}
.controls {
  display: flex;
  justify-content: center;
  margin: 0 5px;
  border: 2px solid;
  border-radius: 10px;
  span {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
  }
}
.volume,
.caption-size {
  width: 110px;
}
.caption-button {
  min-width: 65px;
  border: 2px solid black;
  &.off {
    background-color: black;
  }
}
.mute,
.animations {
  width: 35px;
  border-radius: 20px;
  i {
    font-size: 26px;
  }
  &.disabled {
    border-color: #d3d3d3;
  }
}
span:not(.caption-btn-text):not(.caption-text) {
  border-left: 2px solid;
  border-right: 2px solid;
  padding: 0.2em;
  font-size: 12px;
  min-width: 35px;
}
.captions {
  position: absolute;
  left: 0px;
  bottom: 0px;
  padding: 0.2em 0.5em;
  text-align: center;
  width: 50%;
  font-size: var(--font-Size);
}
.caption-text {
  background: black;
  color: white;
  opacity: 0.8;
  padding: 0;
  &.padded {
    padding: 2px 5px;
  }
}
</style>
